import type { editor } from "monaco-editor";
import { bindThis } from "../Lang.ts";
import { assertNotUndefined, filter, firstItem, notEmpty } from "@de-xima/xima-common-js-lang";
import { allByClassNameHtml, byCss } from "@de-xima/xima-common-js-dom";

type SupportedLanguage = "css" | "javascript" | "html" | "json" | "plaintext" | "xml" | "yaml";

interface ElFinderMonacoState {
  editor: editor.IStandaloneCodeEditor;
}

function isVisible(element: HTMLElement): boolean {
  return (element.offsetHeight > 0 && element.offsetWidth > 0) || element.getClientRects().length > 0;
}

function getLanguageByMime(mime: string): SupportedLanguage {
  switch (mime) {
    case "text/css":
      return "css";
    case "application/xml":
      return "xml";
    case "text/javascript":
      return "javascript";
    case "text/html":
      return "html";
    case "application/json":
      return "json";
    case "application/yaml":
      return "yaml";
    default:
      return "plaintext";
  }
}

function createMonacoOptionsBase(): editor.IStandaloneEditorConstructionOptions {
  return {
    fixedOverflowWidgets: true,
    rulers: [{ column: 80, color: "#d2d2d2" }],
    wordWrap: "wordWrapColumn",
    lineNumbers: "on",
    suggest: {
      shareSuggestSelections: true,
    },
  };
}

function createMonacoOptionsByLanguage(language: SupportedLanguage): editor.IStandaloneEditorConstructionOptions {
  switch (language) {
    case "css":
      return { ...createMonacoOptionsBase(), language: "css", stopRenderingLineAfter: 1000, wordWrapColumn: 10000 };
    case "html":
      return { ...createMonacoOptionsBase(), language: "html" };
    case "javascript":
      return {
        ...createMonacoOptionsBase(),
        language: "javascript",
        showUnused: true,
        showDeprecated: true,
        snippetSuggestions: "bottom",
        suggestSelection: "recentlyUsed",
        suggest: {
          shareSuggestSelections: true,
          showInterfaces: false,
          showKeywords: false,
          showEnums: false,
          showModules: false,
        },
      };
    case "json":
      return { ...createMonacoOptionsBase(), language: "json" };
    case "plaintext":
      return { ...createMonacoOptionsBase(), language: "plaintext" };
    case "xml":
      return { ...createMonacoOptionsBase(), language: "xml" };
    case "yaml":
      return { ...createMonacoOptionsBase(), language: "yaml" };
  }
}

/**
 * Creates a new Monaco editor integrator for ElFinder.
 * @returns New Monaco editor for ElFinder.
 */
export function newMonacoElFinderIntegrator(): elFinder.ElFinderInlineEditorHandler<ElFinderMonacoState, "textarea"> {
  return {
    info: {
      id: "monaco",
      name: "Monaco",
    },
    mimes: [
      "text/plain",
      "text/css",
      "text/javascript",
      "text/html",
      "application/json",
      "application/xml",
      "application/yaml",
    ],
    exts: ["txt", "css", "js", "html", "json", "xml", "yaml"],
    html: "<textarea></textarea>",
    init: bindThis((element, id, file, content) => {
      const textarea = element.get(0);
      assertNotUndefined(textarea);
      textarea.id = id;
      textarea.dataset.mime = file.mime;
      textarea.value = content;
      textarea.style.display = "none";
    }),

    close: (_element, state) => {
      state?.editor.dispose();
    },

    prepare: (_element, overlayDialogSettings) => {
      // Workaround since we do not get access to the elFinder instance in this method
      const elFinderPanel = firstItem(filter(allByClassNameHtml("elfinder"), isVisible));
      const statusBar = byCss(".elfinder-statusbar", elFinderPanel);

      if (elFinderPanel !== undefined && statusBar !== undefined) {
        const panelRect = elFinderPanel.getBoundingClientRect();
        const statusBarRect = statusBar.getBoundingClientRect();
        const width = panelRect.width;
        const height = statusBarRect.y - panelRect.y;
        overlayDialogSettings.position = { left: panelRect.x, top: panelRect.y };
        overlayDialogSettings.width = width;
        overlayDialogSettings.height = `${height}px`;
      } else {
        const width = Math.min(1200, Math.round(window.innerWidth * 0.8));
        const height = Math.min(800, Math.round(window.innerHeight * 0.8));
        overlayDialogSettings.width = width;
        overlayDialogSettings.height = `${height}px`;
      }
    },

    focus: (_element, state) => {
      state?.editor.focus();
    },

    getContent: bindThis((element) => {
      return element.get(0)?.value ?? "";
    }),

    load: (element) => {
      const mime = element.dataset.mime ?? "";
      const language = notEmpty(mime) ? getLanguageByMime(mime) : "plaintext";
      const div = document.createElement("div");
      div.style.width = "100%";
      div.style.height = "100%";
      element.parentElement?.appendChild(div);
      const editor = monaco.editor.create(div, {
        value: element.value,
        ...createMonacoOptionsByLanguage(language),
      });
      return { editor };
    },

    resize: (_element, state) => {
      state?.editor.layout();
    },

    save: (textarea, state) => {
      textarea.value = state?.editor.getValue() ?? "";
    },
  };
}
